import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

export interface AuthConfig {
  methods: string[];
  verification_method?: 'otp' | 'link' | 'both';
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  name?: string;
}

export interface TokenData {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user_id: string;
  email: string;
  name?: string;
  tenant_id: string | null;
  roles: string[];
  role_names: string[];
  permissions: string[];
  subscription_status: string;
  email_verified: boolean;
  onboarding_completed?: boolean;
  /**
   * Persistent per-membership "the user has already reached the SAM chat at least once" flag
   * (backed by `tenant_memberships.sam_welcome_shown`, flipped on the first-ever SAM turn).
   * When true the client skips every onboarding route (plans/setup/profile) — useOnboardingRoute
   * short-circuits so a returning member is never sent back through the wizard just because a
   * per-user checklist step (e.g. voice) is still pending on the tenant doc.
   */
  sam_welcome_shown?: boolean;
}

export type OnboardingStage = 'setup' | 'settings' | 'done';

export interface OnboardingStep {
  step: string;
  label: string;
  order: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  required: boolean;
}

/**
 * Per-user guided-setup step (profile, voice) appended after the tenant steps. Completion is
 * tracked per-membership (user_onboarding_checklist) so the wizard can resume at the correct
 * user step just like tenant steps.
 */
export interface UserOnboardingStep {
  step: string;
  label: string;
  order: number;
  required: boolean;
  status?: 'not_started' | 'in_progress' | 'completed' | 'skipped';
}

export interface OnboardingState {
  stage: OnboardingStage;
  resume_step: string | null;
  settings_visited: boolean;
  /** True once the user chose "Skip to Dashboard" — setup gate is released, resumable later. */
  deferred?: boolean;
  steps: OnboardingStep[];
  /** Per-user steps (profile, voice) shown after the tenant steps — data-driven from config. */
  user_steps?: UserOnboardingStep[];
}

/** A service territory is a free-form area name (e.g. a city or region). */
export type ServiceTerritory = string;

export interface NotificationConfig {
  classifications: string[];
  channels: string[];
  matrix: Record<string, Record<string, boolean>>;
  quiet_hours: { enabled: boolean; from: string; to: string };
}

export interface SystemRole {
  role_id: string;
  name: string;
  description: string;
  type: string;
  permissions: string[];
}

export interface TenantRole {
  tenant_role_id: string;
  tenant_id: string;
  name: string;
  description: string;
  system_role_mappings: string[];
  visibility: boolean;
  is_default: boolean;
}

export interface AssignableRole {
  id: string;
  name: string;
  description: string;
  type: string;
  source: string;
  system_role_mappings?: string[];
  visibility?: boolean;
}

export interface CreateTenantRoleRequest {
  name: string;
  description?: string;
  system_role_mappings: string[];
  visibility: boolean;
}

export interface UpdateTenantRoleRequest {
  name?: string;
  description?: string;
  system_role_mappings?: string[];
  visibility?: boolean;
}

export interface RegisterRequest {
  company_name: string;
  scope_of_work?: string[];
  primary_contact: { name: string; email: string; phone?: string };
  address?: { street?: string; city?: string; state?: string; zip?: string; country?: string };
  timezone?: string;
  legal_accepted?: boolean;
  legal_acceptances?: Array<{ document_key: string; version: string }>;
  password?: string;
  confirm_duplicate?: boolean;
}

export interface InvitationRequest {
  invitee_name?: string;
  invitee_email?: string;
  invitee_phone?: string;
  roles: string[];
  expiry_days?: number;
}

export interface TenantUser {
  user_id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  profile_picture_url?: string | null;
  role_ids: string[];
  role_names: string[];
  status: string;
  joined_at?: string | null;
}

export interface LegalDocument {
  document_key: string;
  version: string;
  title: string;
  content: string;
  effective_date: string;
}

export interface Plan {
  plan_id: string;
  name: string;
  description: string;
  price_monthly: number | null;
  price_annual: number | null;
  trial_eligible?: boolean;
  trial_duration_days?: number;
  user_limit?: number;
  features?: string[];
}

export interface Subscription {
  plan_id: string;
  status: string;
  billing_cycle: string;
  trial_start: string | null;
  trial_end: string | null;
  billing_start: string | null;
}

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Auth', 'Invitation', 'TenantUser', 'Subscription', 'TenantSettings', 'Lifecycle', 'Roles', 'Onboarding', 'Configuration'],
  endpoints: (builder) => ({
    // ── Auth config (public) ──────────────────────────────────────────────────
    getAuthConfig: builder.query<{ success: boolean; data: AuthConfig }, void>({
      query: () => '/api/v1/auth/config',
    }),

    // ── Onboarding config (public) ───────────────────────────────────────────
    getOnboardingConfig: builder.query<{ success: boolean; data: { scope_of_work_types: string[] } }, void>({
      query: () => '/api/v1/onboarding/config',
    }),

    // ── Legal documents (public) ──────────────────────────────────────────────
    getLegalDocuments: builder.query<{ success: boolean; data: LegalDocument[] }, void>({
      query: () => '/api/v1/legal/documents',
    }),

    // ── Native signup ─────────────────────────────────────────────────────────
    signup: builder.mutation<{ success: boolean; data: TokenData }, SignupRequest>({
      query: (body) => ({ url: '/api/v1/auth/signup', method: 'POST', body }),
    }),

    // ── Native login ──────────────────────────────────────────────────────────
    login: builder.mutation<{ success: boolean; data: TokenData }, LoginRequest>({
      query: (body) => ({ url: '/api/v1/auth/login', method: 'POST', body }),
    }),

    // ── Google OAuth (ID token from GoogleLogin component) ─────────────────
    googleCallback: builder.mutation<{ success: boolean; data: TokenData }, { id_token: string }>({
      query: (body) => ({ url: '/api/v1/auth/google', method: 'POST', body }),
    }),

    // ── Password flows ────────────────────────────────────────────────────────
    forgotPassword: builder.mutation<{ success: boolean }, { email: string }>({
      query: (body) => ({ url: '/api/v1/auth/forgot-password', method: 'POST', body }),
    }),
    resetPassword: builder.mutation<{ success: boolean }, { token: string; new_password: string }>({
      query: (body) => ({ url: '/api/v1/auth/reset-password', method: 'POST', body }),
    }),
    logout: builder.mutation<{ success: boolean }, void>({
      query: () => ({ url: '/api/v1/auth/logout', method: 'POST', body: {} }),
    }),
    verifyEmail: builder.mutation<{ success: boolean }, { token: string }>({
      query: (body) => ({ url: '/api/v1/auth/verify-email', method: 'POST', body }),
    }),

    // ── Email OTP verification ────────────────────────────────────────────────
    verifyEmailOtp: builder.mutation<{ success: boolean }, { email: string; otp: string }>({
      query: (body) => ({ url: '/api/v1/auth/verify-otp', method: 'POST', body }),
    }),
    resendEmailOtp: builder.mutation<{ success: boolean }, { email: string }>({
      query: (body) => ({ url: '/api/v1/auth/resend-otp', method: 'POST', body }),
    }),

    // ── Tenant registration ───────────────────────────────────────────────────
    register: builder.mutation<
      { success: boolean; message_code: number; data: Record<string, unknown> },
      RegisterRequest
    >({
      query: (body) => ({ url: '/api/v1/tenants/register', method: 'POST', body }),
    }),

    // ── Setup checklist ───────────────────────────────────────────────────────
    getSetupChecklist: builder.query<{ success: boolean; data: Record<string, unknown> }, void>({
      query: () => '/api/v1/onboarding/checklist',
      providesTags: ['Onboarding'],
    }),
    updateChecklistStep: builder.mutation<{ success: boolean }, { step: string; status: string }>({
      query: ({ step, status }) => ({
        url: `/api/v1/onboarding/checklist/${step}`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Onboarding'],
    }),

    // ── Onboarding state machine (authoritative routing source) ─────────────────
    getOnboardingState: builder.query<{ success: boolean; data: OnboardingState }, void>({
      query: () => '/api/v1/onboarding/state',
      providesTags: ['Onboarding'],
    }),
    markSettingsVisited: builder.mutation<{ success: boolean; data: OnboardingState }, void>({
      query: () => ({ url: '/api/v1/onboarding/settings-visited', method: 'POST' }),
      invalidatesTags: ['Onboarding'],
    }),
    // "Skip to Dashboard" — release the setup gate so the user can explore now and finish later.
    deferOnboarding: builder.mutation<{ success: boolean; data: OnboardingState }, void>({
      query: () => ({ url: '/api/v1/onboarding/defer', method: 'POST' }),
      invalidatesTags: ['Onboarding'],
    }),

    // ── Invitations ───────────────────────────────────────────────────────────
    sendInvitation: builder.mutation<
      { success: boolean; message_code: number; data: { invitation_id: string; email_status: 'pending' | 'none' } },
      InvitationRequest
    >({
      query: (body) => ({ url: '/api/v1/invitations', method: 'POST', body }),
      invalidatesTags: ['Invitation'],
    }),
    listInvitations: builder.query<
      { success: boolean; data: unknown[]; total: number },
      { status?: string } | void
    >({
      query: (params) => {
        const status = (params as { status?: string })?.status;
        return `/api/v1/invitations${status ? `?status=${status}` : ''}`;
      },
      providesTags: ['Invitation'],
    }),
    getInvitationByToken: builder.query<
      { success: boolean; data: Record<string, unknown> },
      string
    >({
      query: (token) => `/api/v1/invitations/accept/${token}`,
    }),
    acceptInvitation: builder.mutation<
      { success: boolean },
      { token: string; name?: string; phone?: string; timezone?: string; password?: string; google_id_token?: string }
    >({
      query: (body) => ({ url: '/api/v1/invitations/accept', method: 'POST', body }),
    }),
    revokeInvitation: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/api/v1/invitations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Invitation'],
    }),
    resendInvitation: builder.mutation<
      { success: boolean; message_code: number; data: { email_status: 'pending' | 'none' } },
      string
    >({
      query: (id) => ({ url: `/api/v1/invitations/${id}/resend`, method: 'POST', body: {} }),
      invalidatesTags: ['Invitation'],
    }),

    // ── Tenant users (team directory) ─────────────────────────────────────────
    listTenantUsers: builder.query<
      { success: boolean; data: TenantUser[]; total: number; page: number; page_size: number },
      { search?: string; role?: string; status?: string; page?: number; page_size?: number } | void
    >({
      query: (params) => {
        const p = (params ?? {}) as { search?: string; role?: string; status?: string; page?: number; page_size?: number };
        const qs = new URLSearchParams();
        if (p.search) qs.set('search', p.search);
        if (p.role) qs.set('role', p.role);
        if (p.status) qs.set('status', p.status);
        if (p.page) qs.set('page', String(p.page));
        if (p.page_size) qs.set('page_size', String(p.page_size));
        const q = qs.toString();
        return `/api/v1/users${q ? `?${q}` : ''}`;
      },
      providesTags: ['TenantUser'],
    }),
    getTenantUserById: builder.query<TenantUser, string>({
      query: (user_id) => `/api/v1/users/${user_id}`,
      transformResponse: (res: { success: boolean; data: TenantUser }) => res.data,
      providesTags: (_r, _e, user_id) => [{ type: 'TenantUser' as const, id: user_id }],
    }),
    updateTenantUser: builder.mutation<
      { success: boolean; data: TenantUser },
      { user_id: string; name?: string; phone?: string; role_ids?: string[]; status?: string }
    >({
      query: ({ user_id, ...body }) => ({ url: `/api/v1/users/${user_id}`, method: 'PUT', body }),
      invalidatesTags: ['TenantUser'],
    }),

    // ── Plans & subscriptions ─────────────────────────────────────────────────
    listPlans: builder.query<{ success: boolean; data: Plan[]; total: number }, void>({
      query: () => '/api/v1/subscriptions/plans',
    }),
    selectPlan: builder.mutation<
      { success: boolean; data: Subscription },
      { plan_id: string; billing_cycle: string }
    >({
      query: (body) => ({ url: '/api/v1/subscriptions/activate', method: 'POST', body }),
      invalidatesTags: ['Subscription'],
    }),
    getSubscription: builder.query<{ success: boolean; data: Subscription }, void>({
      query: () => '/api/v1/subscriptions/me',
      providesTags: ['Subscription'],
    }),

    // ── Tenant settings ───────────────────────────────────────────────────────
    getTenantSettings: builder.query<{ success: boolean; data: Record<string, unknown> }, void>({
      query: () => '/api/v1/tenants/me',
      providesTags: ['TenantSettings'],
    }),
    // Single mutation for all tenant updates (profile, branding, defaults) —
    // all hit PUT /api/v1/tenants/me with a partial tenant body.
    updateTenant: builder.mutation<{ success: boolean }, Record<string, unknown>>({
      query: (body) => ({ url: '/api/v1/tenants/me', method: 'PUT', body }),
      invalidatesTags: ['TenantSettings'],
    }),

    // ── Generic configuration (catalogue-backed; layer-aware read/write) ───────
    // Used for catalogue-driven settings such as the notification delivery matrix and quiet
    // hours. Reads return the merged effective value; writes route to the tenant layer.
    getConfiguration: builder.query<
      { success: boolean; data: { effective_value: Record<string, unknown> } & Record<string, unknown> },
      string
    >({
      query: (key) => `/api/v1/configurations/${key}`,
      providesTags: (_r, _e, key) => [{ type: 'Configuration', id: key }],
    }),
    updateConfiguration: builder.mutation<
      { success: boolean; message_code: number },
      { key: string; value: Record<string, unknown> }
    >({
      query: ({ key, value }) => ({ url: `/api/v1/configurations/${key}`, method: 'PUT', body: { value } }),
      invalidatesTags: (_r, _e, { key }) => [{ type: 'Configuration', id: key }],
    }),

    // ── Per-user onboarding step tracking ────────────────────────────────────
    updateUserStep: builder.mutation<{ success: boolean; data: OnboardingState }, { step: string; status: string }>({
      query: ({ step, status }) => ({
        url: `/api/v1/onboarding/user-step/${step}`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Onboarding'],
    }),

    // ── Phone OTP ─────────────────────────────────────────────────────────────
    sendPhoneOtp: builder.mutation<{ success: boolean }, { phone: string }>({
      query: (body) => ({ url: '/api/v1/onboarding/phone/send-otp', method: 'POST', body }),
    }),
    verifyPhoneOtp: builder.mutation<{ success: boolean }, { otp_code: string }>({
      query: (body) => ({ url: '/api/v1/onboarding/phone/verify-otp', method: 'POST', body }),
    }),

    // ── RBAC ──────────────────────────────────────────────────────────────────
    listSystemRoles: builder.query<{ success: boolean; data: SystemRole[] }, void>({
      query: () => '/api/v1/roles/system',
      providesTags: ['Roles'],
    }),
    listTenantRoles: builder.query<{ success: boolean; data: TenantRole[] }, void>({
      query: () => '/api/v1/roles/tenant',
      providesTags: ['Roles'],
    }),
    listAssignableRoles: builder.query<{ success: boolean; data: AssignableRole[] }, void>({
      query: () => '/api/v1/roles/assignable',
      providesTags: ['Roles'],
    }),
    createTenantRole: builder.mutation<{ success: boolean }, CreateTenantRoleRequest>({
      query: (body) => ({ url: '/api/v1/roles/tenant', method: 'POST', body }),
      invalidatesTags: ['Roles'],
    }),
    updateTenantRole: builder.mutation<{ success: boolean }, { id: string; data: UpdateTenantRoleRequest }>({
      query: ({ id, data }) => ({ url: `/api/v1/roles/tenant/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['Roles'],
    }),
    deleteTenantRole: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/api/v1/roles/tenant/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Roles'],
    }),
    updateSystemRolePermissions: builder.mutation<{ success: boolean }, { roleId: string; permissions: string[] }>({
      query: ({ roleId, permissions }) => ({ url: `/api/v1/roles/system/${roleId}/permissions`, method: 'PUT', body: { permissions } }),
      invalidatesTags: ['Roles'],
    }),

    // ── Lifecycle ─────────────────────────────────────────────────────────────
    getLifecycle: builder.query<
      { success: boolean; data: { lifecycle_status: string; lifecycle_history: unknown[] } },
      void
    >({
      query: () => '/api/v1/tenants/me/lifecycle',
      providesTags: ['Lifecycle'],
    }),
    updateLifecycle: builder.mutation<
      { success: boolean },
      { status: string; reason?: string }
    >({
      query: (body) => ({ url: '/api/v1/tenants/me/lifecycle', method: 'PUT', body }),
      invalidatesTags: ['Lifecycle'],
    }),
  }),
});

export const {
  useGetAuthConfigQuery,
  useGetOnboardingConfigQuery,
  useGetLegalDocumentsQuery,
  useSignupMutation,
  useLoginMutation,
  useGoogleCallbackMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
  useVerifyEmailMutation,
  useVerifyEmailOtpMutation,
  useResendEmailOtpMutation,
  useRegisterMutation,
  useGetSetupChecklistQuery,
  useUpdateChecklistStepMutation,
  useGetOnboardingStateQuery,
  useMarkSettingsVisitedMutation,
  useDeferOnboardingMutation,
  useSendInvitationMutation,
  useListInvitationsQuery,
  useGetInvitationByTokenQuery,
  useAcceptInvitationMutation,
  useRevokeInvitationMutation,
  useResendInvitationMutation,
  useListTenantUsersQuery,
  useGetTenantUserByIdQuery,
  useUpdateTenantUserMutation,
  useListPlansQuery,
  useSelectPlanMutation,
  useGetSubscriptionQuery,
  useGetTenantSettingsQuery,
  useUpdateTenantMutation,
  useGetConfigurationQuery,
  useUpdateConfigurationMutation,
  useGetLifecycleQuery,
  useUpdateLifecycleMutation,
  useUpdateUserStepMutation,
  useSendPhoneOtpMutation,
  useVerifyPhoneOtpMutation,
  useListSystemRolesQuery,
  useListTenantRolesQuery,
  useListAssignableRolesQuery,
  useCreateTenantRoleMutation,
  useUpdateTenantRoleMutation,
  useDeleteTenantRoleMutation,
  useUpdateSystemRolePermissionsMutation,
} = onboardingApi;
